import type { ComponentProps } from "react";

import { useGetAsset } from "~/v2/api/hooks";
import { useGetQuotes } from "~/v2/api/hooks/useGetQuotes";
import { useMarketDataFeed } from "~/v2/api/hooks/useMarketDataFeed";
import type { Order } from "~/v2/api/rest/account";
import type { ReplaceAtdOrderPayload } from "./api";
import {
  useOrderReplacementController,
  useOrderReplacementMutationController,
} from "./OrderReplacementDialog.controller";
import {
  OrderReplacementDialogPresentation,
  type OrderReplacementDialogPresentationProps,
  OrderReplacementVariantPresentation,
} from "./OrderReplacementDialog.pres";
import { OrderReplacePanelPresentation } from "./OrderReplacePanel.pres";

const useReplacePanelMarket = (order?: Order | null) => {
  const symbol = order?.symbol?.trim() ?? "";
  const { latestFeed } = useMarketDataFeed();
  const { quotes } = useGetQuotes({
    symbols: symbol ? [symbol] : [],
    feed: latestFeed,
    options: { enabled: Boolean(symbol) },
  });
  const { asset } = useGetAsset({
    symbol,
    options: { enabled: Boolean(symbol) },
  });
  const quote = quotes?.find((item) => item.symbol === symbol);
  return {
    assetName: asset?.name,
    lastPrice: quote?.last,
  };
};

const OrderReplacePanelWithMarket = (
  props: Omit<
    ComponentProps<typeof OrderReplacePanelPresentation>,
    "assetName" | "lastPrice"
  >,
) => {
  const market = useReplacePanelMarket(props.order);
  return <OrderReplacePanelPresentation {...props} {...market} />;
};

export interface OrderReplacementIdentity {
  accountID: string;
  product: "live" | "paper";
  orderID: string;
}

export type OrderReplacementSubmittedResult = {
  original: OrderReplacementIdentity;
} & ({ replacementOrder: Order } | { replacementOrder: null });

export interface OrderReplacementDialogProps {
  open: boolean;
  variant?: "dialog" | "inline";
  accountID?: string;
  product?: "live" | "paper";
  orderID?: string;
  onOpenChange: (open: boolean) => void;
  onSubmitted: (result: OrderReplacementSubmittedResult) => void;
  onMutationPendingChange?: (orderID: string, pending: boolean) => void;
  onCloseAutoFocus?: OrderReplacementDialogPresentationProps["onCloseAutoFocus"];
}

interface LoadedReplacementDialogProps
  extends Omit<
    OrderReplacementDialogPresentationProps,
    "controller" | "order"
  > {
  order: Order;
  onConfirm: (payload: ReplaceAtdOrderPayload) => void;
}

const LoadedReplacementDialog = ({
  order,
  onConfirm,
  ...presentationProps
}: LoadedReplacementDialogProps) => {
  const controller = useOrderReplacementController({ order, onConfirm });
  return (
    <OrderReplacementDialogPresentation
      {...presentationProps}
      order={order}
      controller={controller}
    />
  );
};

const LoadedReplacementInline = ({
  order,
  onConfirm,
  isPending,
  isLoading,
  isError,
  errorMessage,
  submissionError,
  eligibilityDisabledReason,
  onOpenChange,
  onRetry,
}: LoadedReplacementDialogProps) => {
  const controller = useOrderReplacementController({ order, onConfirm });
  return (
    <OrderReplacePanelWithMarket
      order={order}
      controller={controller}
      isReplacing={isPending}
      isLoading={isLoading}
      isError={isError}
      errorMessage={submissionError ?? errorMessage}
      eligibilityDisabledReason={eligibilityDisabledReason}
      onRetry={onRetry}
      onClose={() => onOpenChange(false)}
    />
  );
};

type ReplacementPresentationProps = Omit<
  OrderReplacementDialogPresentationProps,
  "controller" | "order"
>;

export const OrderReplacementDialog = ({
  open,
  variant = "dialog",
  accountID,
  product,
  orderID,
  onOpenChange,
  onSubmitted,
  onMutationPendingChange,
  onCloseAutoFocus,
}: OrderReplacementDialogProps) => {
  const mutation = useOrderReplacementMutationController({
    open,
    accountID,
    product,
    orderID,
    onOpenChange,
    onSubmitted,
    onMutationPendingChange,
  });

  const commonPresentationProps = {
    open,
    eligibilityDisabledReason: mutation.eligibilityDisabledReason,
    isLoading: mutation.isLoading,
    isFetching: mutation.isFetching,
    isError: mutation.isError,
    isNotFound: mutation.isNotFound,
    isPending: mutation.isPending,
    errorMessage: mutation.errorMessage,
    submissionError: mutation.submissionError,
    onOpenChange: mutation.onOpenChange,
    onCloseAutoFocus,
    onRetry: mutation.onRetry,
  } satisfies ReplacementPresentationProps;

  const dialog =
    mutation.canEdit && mutation.order ? (
      <LoadedReplacementDialog
        {...commonPresentationProps}
        order={mutation.order}
        onConfirm={mutation.onConfirm}
      />
    ) : (
      <OrderReplacementDialogPresentation
        {...commonPresentationProps}
        order={mutation.order}
      />
    );

  const inline =
    mutation.canEdit && mutation.order ? (
      <LoadedReplacementInline
        {...commonPresentationProps}
        order={mutation.order}
        onConfirm={mutation.onConfirm}
      />
    ) : (
      <OrderReplacePanelWithMarket
        order={mutation.order}
        isReplacing={commonPresentationProps.isPending}
        isLoading={commonPresentationProps.isLoading}
        isError={commonPresentationProps.isError}
        errorMessage={
          commonPresentationProps.submissionError ??
          commonPresentationProps.errorMessage
        }
        eligibilityDisabledReason={
          commonPresentationProps.eligibilityDisabledReason
        }
        onRetry={commonPresentationProps.onRetry}
        onClose={() => commonPresentationProps.onOpenChange(false)}
      />
    );

  return (
    <OrderReplacementVariantPresentation
      variant={variant}
      open={open}
      dialog={dialog}
      inline={inline}
    />
  );
};
